import { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import React, { Fragment, useMemo } from 'react';
import { Row } from 'react-table';

import useLongPress from '../../hooks/useLongPress';
import { useTableStateBag } from '../../hooks/useTableStateBag';
import { TableRow } from '../../types';
import { SELECT_BOX_MARKER } from '../../utils/makeRowsSelectable';
import { ILoaderProps } from '../loader/Loader';
import { TableSkeletonRow } from '../table-skeleton-row/TableSkeletonRow';

import { Row as RowComponent } from './components/row/Row';
import { SEPARATOR_ROW_HEIGHT, SeparatorRow } from './components/SeparatorRow';

interface RenderRowProps
  extends Pick<
    ILoaderProps,
    | 'classes'
    | 'cardsView'
    | 'cardConfig'
    | 'onRowClick'
    | 'onRowDoubleClick'
    | 'onRowContextMenu'
    | 'ignoreLongPress'
    | 'prepareRow'
  > {
  height: number;
  row: TableRow;
  virtualRow?: VirtualItem;
  rowVirtualizer?: Virtualizer<HTMLDivElement, HTMLDivElement>;
}

export const RenderRow = (props: RenderRowProps) => {
  const {
    height,
    row,
    virtualRow,
    rowVirtualizer,
    classes,
    cardsView = false,
    cardConfig = null,
    onRowClick,
    onRowDoubleClick,
    onRowContextMenu,
    ignoreLongPress = false,
    prepareRow,
  } = props;

  // Table's state is not actually used here, but it is passed as prop to the
  // memoized Row component to avoid rendering outdated view.
  // If rendering outside of context provider just use an empty object as it
  // will always force a re-render.
  const { tableState = {}, setUnderPressSelect } = useTableStateBag() ?? {};

  const rowEventHandlers = {
    onContextMenu(event: React.MouseEvent<HTMLDivElement>) {
      const { nativeEvent } = event;

      if (
        nativeEvent instanceof PointerEvent &&
        nativeEvent.pointerType === 'touch'
      ) {
        event.preventDefault();
      } else {
        onRowContextMenu?.(event, row);
      }
    },
    onDoubleClick: handleRowDoubleClick,
    ...useLongPress({
      onClick(event) {
        onRowClick?.(event, row);
      },
      onLongPress() {
        if (ignoreLongPress) return;
        setUnderPressSelect?.(true);
        row.toggleRowSelected();
      },
    }),
  };

  const isCardsView = Boolean(cardsView && cardConfig);

  const style: React.CSSProperties = {
    width: '100%',

    ...(virtualRow && {
      position: 'absolute',
      transform: `translateY(${virtualRow.start}px)`,
    }),
  };

  const rowStyle = useMemo(
    () => ({ height: isCardsView ? undefined : height }),
    [height, isCardsView]
  );

  if (!row || row.original.skeleton) {
    return <TableSkeletonRow style={{ ...style, height }} />;
  }

  prepareRow(row as unknown as Row);

  const rowProps = row.getRowProps({ style });

  function handleRowDoubleClick(event: React.MouseEvent<HTMLElement>) {
    if (!onRowDoubleClick) {
      return;
    }

    // Ignore double clicks on the checkbox element

    const target = event.target as HTMLElement;

    if (target.closest(`[data-${SELECT_BOX_MARKER}=true]`)) {
      return;
    }

    // Ignore double clicks if any of the modifier keys are pressed (Ctrl, Shift, Command)
    if (event.ctrlKey || event.shiftKey || event.metaKey) {
      return;
    }

    onRowDoubleClick(row.id);
  }

  const isSectionStart = row?.original.meta?.isSectionStart ?? false;
  if (!isSectionStart) {
    return renderRow();
  }

  return (
    <Fragment key={virtualRow?.key ?? row.id}>
      {renderSectionStartRow()}
      {renderRow()}
    </Fragment>
  );

  function renderSectionStartRow() {
    const sectionLabel = row.original.meta.sectionLabel ?? '';
    return <SeparatorRow label={sectionLabel} rowStyle={style} />;
  }

  function renderRow() {
    return (
      <div
        {...rowEventHandlers}
        {...rowProps}
        key={virtualRow?.key ?? row.id}
        data-index={virtualRow?.index} // needed for dynamic row height measurement
        data-section-start={isSectionStart || null}
        ref={
          isCardsView && rowVirtualizer ? rowVirtualizer.measureElement : null
        } // measure dynamic row height (only for cards)
        style={{
          ...rowProps.style,
          transform: virtualRow
            ? `translateY(${
                virtualRow.start + (isSectionStart ? SEPARATOR_ROW_HEIGHT : 0)
              }px)`
            : null,
          // If the row is the footer for a group, we disable the pointer-events like `onClick`.
          ...(row.original.meta?.isGroupFooter && { pointerEvents: 'none' }),
        }}
      >
        <RowComponent
          row={row}
          tableState={tableState}
          classes={classes}
          cardsView={cardsView}
          cardConfig={cardConfig}
          isGroupFooter={row.original.meta?.isGroupFooter}
          style={rowStyle}
        />
      </div>
    );
  }
};
